import React from 'react';
import { bool, func, string, node, oneOfType, object } from 'prop-types';

import withMobileDialog from '@material-ui/core/withMobileDialog';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { LinkButton, Button } from '@lavu-pos/stellar';
import { FormattedMessage } from 'react-intl';
import { CircularProgress, Spacer } from 'modules/utils';

import { DialogStyle, HeaderDialog } from './styles';
import { dateFormatter, TIME_WITH_PERIOD_FORMAT_FNS } from 'modules/utils';

const propTypes = {
	fullScreen: bool,
	customwidth: string,
	open: bool.isRequired,
	onClose: func,
	onSave: func,
	saveDisabled: bool,
	/* Will no render Cancel button if onCancel is not defined */
	onCancel: func,
	elementIdPrefix: string.isRequired,
	title: oneOfType([node, string]).isRequired,
	subtitle: oneOfType([node, string]),
	children: node,
	leftButtonContent: oneOfType([node, string]),
	leftButtonAction: func,
	saveText: node,
	cancelText: node,
	saveLoading: bool,
	cancelLoading: bool,
	cancelDisabled: bool,
	hideCloseIcon: bool,
	hideCancelButton: bool,
	hideActions: bool,
	DialogProps: object,
	restoreInfo: object,
};

const defaultProps = {
	saveDisabled: false,
	cancelDisabled: false,
	subtitle: '',
	leftButtonContent: '',
	leftButtonAction: Function.prototype,
	saveText: 'fieldSave',
	cancelText: 'fieldCancel',
	saveLoading: false,
	cancelLoading: false,
	hideActions: false,
};

const renderModalSection = section =>
	typeof section === 'string' ? <FormattedMessage id={section} /> : section;

/**
 * Modal used for the Kiosk Settings screen as a generic modal.
 * This modal can be reused for different scenarios inside of Kiosk Settings.
 * It was added after checkout fields, so it borrows a lot of styles from there (it's quite similar).
 */
const SettingsModal = ({
	fullScreen,
	customwidth,
	open,
	onClose = Function.prototype,
	onSave = Function.prototype,
	saveText,
	cancelText,
	cancelLoading,
	cancelDisabled,
	saveDisabled,
	saveLoading,
	onCancel = Function.prototype,
	elementIdPrefix,
	title,
	subtitle,
	children,
	leftButtonContent,
	leftButtonAction,
	hideActions = false,
	hideCloseIcon = false,
	hideCancelButton = false,
	DialogProps = {},
	restoreInfo,
}) => {
	return (
		<DialogStyle
			fullScreen={fullScreen}
			customwidth={customwidth}
			open={open}
			onClose={onClose}
			id={`${elementIdPrefix}-modal`}
			leftButtonContent={leftButtonContent}
			BackdropProps={{ classes: { root: 'settings-modal-backdrop' } }}
			{...DialogProps}
		>
			<div
				className="scrollable"
				id={`${elementIdPrefix}-modal-scrollable-area`}
			>
				<div className="header">
					<HeaderDialog fullScreen={fullScreen}>
						<div id={`${elementIdPrefix}-modal-title`} className="title">
							{renderModalSection(title)}
						</div>
						{!hideCloseIcon && (
							<IconButton
								onClick={onClose}
								color="inherit"
								id={`${elementIdPrefix}-modal-close`}
							>
								<CloseIcon fontSize="large" />
							</IconButton>
						)}
					</HeaderDialog>
					{subtitle && (
						<div id={`${elementIdPrefix}-modal-subtitle`} className="subtitle">
							{renderModalSection(subtitle)}
						</div>
					)}
					
					{restoreInfo && (
						<>
							<div id={`${elementIdPrefix}-modal-subtitle`} className="subtitle">
								{renderModalSection(`Restore End Time : ${dateFormatter(new Date(restoreInfo.restoreEndtime), {
								timeFormat: TIME_WITH_PERIOD_FORMAT_FNS,
							})}`)}
							</div>
							<div id={`${elementIdPrefix}-modal-subtitle`} className="subtitle">
								{renderModalSection(`Restore User : ${restoreInfo.restoreUser}`)}
							</div>
						</>
					)}								
				</div>

				<div className="content">{children}</div>
			</div>
			{!hideActions && (
				<div className="actions">
					{leftButtonContent ? (
						<Button
							id={`${elementIdPrefix}-modal-left-button`}
							size={fullScreen ? 'medium' : 'small'}
							type="default"
							onClick={leftButtonAction}
							className="modalLeftButton"
						>
							{leftButtonContent}
						</Button>
					) : (
						<div />
					)}
					<div className="actionsRight">
						{!hideCancelButton && (
							<LinkButton
								type="default"
								id={`${elementIdPrefix}-modal-cancel-button`}
								className="modalCancelButton"
								disabled={cancelDisabled}
								onClick={onCancel}
							>
								{cancelLoading && (
									<>
										<CircularProgress centered small />
										<Spacer width={5} />
									</>
								)}
								{renderModalSection(cancelText)}
							</LinkButton>
						)}
						<Button
							id={`${elementIdPrefix}-modal-save-button`}
							size={fullScreen ? 'medium' : 'small'}
							className="modalSaveButton"
							disabled={saveDisabled}
							fullWidth={fullScreen}
							onClick={onSave}
							color="primary"
						>
							{saveLoading && (
								<>
									<CircularProgress centered small />
									<Spacer width={5} />
								</>
							)}
							{renderModalSection(saveText)}
						</Button>
					</div>
				</div>
			)}
		</DialogStyle>
	);
};

SettingsModal.propTypes = propTypes;

SettingsModal.defaultProps = defaultProps;

export default withMobileDialog()(SettingsModal);
